/**
 * @fileoverview Application entry point and root configuration.
 *
 * This file bootstraps the React application and configures:
 * - React 18 root rendering
 * - Auth0 authentication provider with token refresh
 * - Performance monitoring with Web Vitals
 *
 * @description The Auth0Provider wraps the entire application to provide
 * authentication context throughout the component tree. Configuration
 * is loaded from environment variables for security.
 *
 * Environment variables required:
 * - REACT_APP_AUTH0_DOMAIN: Auth0 tenant domain
 * - REACT_APP_AUTH0_CLIENT_ID: Application client ID
 * - REACT_APP_AUTH0_REDIRECT_URI: Post-login redirect URL
 * - REACT_APP_AUTH0_AUDIENCE: API audience identifier
 */

import React from 'react';
import ReactDOM from 'react-dom/client';

// CSS Architecture - Import order matters!
import './styles/variables.css';  // Design system tokens (colors, spacing, etc.)
import './styles/themes.css';     // Color theme definitions (blue, green, purple, etc.)
import './styles/base.css';       // Global resets and base styles
import './styles/utilities.css';  // Reusable utility classes
import './index.css';             // Legacy styles (will be migrated)

import App from './App';
import reportWebVitals from './reportWebVitals';
import { Auth0Provider } from '@auth0/auth0-react';
import { ThemeProvider } from './components/ThemeProvider/ThemeProvider';
import { RealtimeProvider } from './contexts/RealtimeContext';

// Auth0 configuration with fallbacks
const auth0Config = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN,
  clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
  authorizationParams: {
    redirect_uri: process.env.REACT_APP_AUTH0_REDIRECT_URI || window.location.origin,
    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    scope: "openid profile email offline_access"  // offline_access enables refresh tokens
  },
  cacheLocation: "localstorage",  // Persist tokens across page refreshes
  useRefreshTokens: true  // Enable silent token refresh
};

// Log configuration at startup (helps diagnose production issues)
console.log('[AUTH0 INIT] Configuration:', {
  domain: auth0Config.domain,
  clientId: auth0Config.clientId?.substring(0, 10) + '...',
  redirect_uri: auth0Config.authorizationParams.redirect_uri,
  audience: auth0Config.authorizationParams.audience,
  scope: auth0Config.authorizationParams.scope,
  cacheLocation: auth0Config.cacheLocation,
  useRefreshTokens: auth0Config.useRefreshTokens
});

// Validate required configuration
if (!auth0Config.domain || !auth0Config.clientId) {
  console.error('[AUTH0 INIT] CRITICAL: Missing required Auth0 configuration!', {
    hasDomain: !!auth0Config.domain,
    hasClientId: !!auth0Config.clientId,
    hasAudience: !!auth0Config.authorizationParams.audience
  });
}

// Create React 18 root and render the application
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider>
      <Auth0Provider {...auth0Config}>
        <RealtimeProvider>
          <App />
        </RealtimeProvider>
      </Auth0Provider>
    </ThemeProvider>
  </React.StrictMode>
);

// Optional: Enable Web Vitals performance monitoring
// Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
